import { useEffect, useCallback, useRef } from 'react';
import { useWebSocketService } from '../../../hooks/useWebSocketService';
import { niiService } from '../../../services/niiService';
import { NiiRunStatus, NiiRunWsEvent } from '../types/nii.types';

/**
 * Hook that listens for NII run status changes via WebSocket.
 *
 * - Subscribes to `NII_RUN` entity changes on the WebSocket
 * - On page load / WS reconnect, calls the run endpoint once per active run to catch up
 * - Returns nothing directly — calls `onRunStatusChange` callback when a run status updates
 *
 * @param runIds - IDs of runs currently displayed (to filter relevant events)
 * @param onRunStatusChange - Callback invoked when a run's status changes
 */
export function useNiiRunWebSocket(runIds: string[], onRunStatusChange: (update: NiiRunWsEvent) => void) {
  const ws = useWebSocketService();
  const onRunStatusChangeRef = useRef(onRunStatusChange);
  onRunStatusChangeRef.current = onRunStatusChange;

  const runIdsRef = useRef(runIds);
  runIdsRef.current = runIds;

  // Subscribe to WebSocket NII_RUN entity changes
  useEffect(() => {
    if (!ws || runIds.length === 0) return;

    const unsubscribe = ws.onEntityChange('NII_RUN', (data: unknown) => {
      const event = data as {
        entity: string;
        action?: string;
        payload?: NiiRunWsEvent;
      };

      const payload = event?.payload;
      if (!payload?.runId || !payload?.status) return;

      // Only process events for runs we're currently displaying
      if (runIdsRef.current.includes(payload.runId)) {
        onRunStatusChangeRef.current(payload);
      }
    });

    return unsubscribe;
  }, [ws, runIds.length]);

  // On mount / when run IDs change, do a one-time catch-up for active runs
  const syncActiveRuns = useCallback(async (ids: string[]) => {
    // Only poll runs with real UUIDs (skip optimistic temporary IDs)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const realIds = ids.filter((id) => uuidRegex.test(id));

    for (const runId of realIds) {
      try {
        const run = await niiService.getRun(runId);
        // If the run has reached a terminal state, notify the consumer
        if (run.status !== NiiRunStatus.QUEUED && run.status !== NiiRunStatus.RUNNING) {
          onRunStatusChangeRef.current({
            runId: run.runId,
            type: run.type,
            status: run.status,
            releaseName: run.releaseName,
            gplVersion: run.gplVersion,
            completedAt: run.completedAt
          });
        }
      } catch {
        // Silently ignore — we'll get the update via WebSocket eventually
      }
    }
  }, []);

  // Catch-up: on mount, sync any active runs
  useEffect(() => {
    if (runIds.length === 0) return;
    syncActiveRuns(runIds);
  }, [runIds.length, syncActiveRuns]);
}
